import { asCompanyId } from "@repo/app-types/entities/company";
import {
  asGroupId,
  checkIsManagerGroup,
  type Group,
  type GroupId,
} from "@repo/app-types/entities/group";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { v4 as uuidv4 } from "uuid";
import {
  buildEntityTag,
  buildListTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

type AllTeammateGroupsArgs = { includeDeactivated?: boolean } | void;

const groupApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Teammate group hooks
    allTeammateGroups: builder.query<
      Group[],
      AllTeammateGroupsArgs,
      { data: { groups: Group[] } }
    >({
      query: (args) => ({
        url: "/groups",
        params: args?.includeDeactivated
          ? { include_deactivated: "true" }
          : undefined,
      }),
      transformResponse: (response) => response.data.groups,
      providesTags: (result) => getTagsProvidedByList(TagType.Group, result),
    }),
    createTeammateGroup: builder.mutation<
      Group,
      { name: string; teammate_ids?: readonly TeammateId[] },
      { data: { group: Group } }
    >({
      query: (body) => ({ method: "POST", url: "/groups", body }),
      transformResponse: (response) => response.data.group,
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.Group),
    }),
    updateTeammateGroup: builder.mutation<
      Group,
      { id: GroupId; name?: string; teammate_ids?: readonly TeammateId[] },
      { data: { group: Group } }
    >({
      query: (values) => ({
        method: "PATCH",
        url: `/groups/${encodeURIComponent(values.id)}`,
        body: values,
      }),
      transformResponse: (response) => response.data.group,
      invalidatesTags: (result, error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.Group, id),
    }),
    deleteTeammateGroup: builder.mutation<void, GroupId>({
      query: (id) => ({
        method: "DELETE",
        url: `/groups/${encodeURIComponent(id)}`,
      }),
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.Group, id),
    }),
    setTeammateManager: builder.mutation<
      { affectedGroups: Group[] },
      { teammateId: TeammateId; managerId: TeammateId | null },
      { data: { groups: Group[] } }
    >({
      query: ({ teammateId, managerId }) => ({
        method: "PUT",
        url: `/teammates/${encodeURIComponent(teammateId)}/managers`,
        body: { managers: managerId !== null ? [{ id: managerId }] : [] },
      }),
      transformResponse: (response) => ({
        affectedGroups: response.data.groups,
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        const optimisticGroupId = asGroupId(uuidv4());
        const cachedArgs = groupApi.util.selectCachedArgsForQuery(
          getState(),
          "allTeammateGroups",
        );
        const optimisticUpdates = cachedArgs.map((queryArgs) =>
          dispatch(
            optimisticallySetTeammateManagerAction(
              queryArgs,
              optimisticGroupId,
              arg.teammateId,
              arg.managerId,
            ),
          ),
        );
        try {
          const {
            data: { affectedGroups },
          } = await queryFulfilled;
          for (const queryArgs of cachedArgs) {
            dispatch(
              handleSetTeammateManagerResultAction(
                queryArgs,
                optimisticGroupId,
                affectedGroups,
              ),
            );
          }
        } catch {
          for (const optimisticUpdate of optimisticUpdates) {
            optimisticUpdate.undo();
          }
        }
      },
      invalidatesTags: (result) => [
        buildListTag(TagType.Group, []),
        ...(result?.affectedGroups ?? []).map((group) =>
          buildEntityTag(TagType.Group, group.id),
        ),
      ],
    }),
  }),
});

function optimisticallySetTeammateManagerAction(
  queryArgs: AllTeammateGroupsArgs,
  optimisticGroupId: GroupId,
  teammateId: TeammateId,
  managerId: TeammateId | null,
) {
  return groupApi.util.updateQueryData(
    "allTeammateGroups",
    queryArgs,
    (draft) => {
      // Remove the teammate from all manager groups
      for (const group of draft) {
        if (
          checkIsManagerGroup(group) &&
          group.teammate_ids.includes(teammateId)
        ) {
          group.teammate_ids = group.teammate_ids.filter(
            (id) => id !== teammateId,
          );
        }
      }

      if (managerId === null) {
        return;
      }

      // Add the teammate to the existing manager group, if we have it
      const existingManagerGroup = draft.find(
        (g) => g.manager_id === managerId,
      );
      if (existingManagerGroup) {
        existingManagerGroup.teammate_ids.push(teammateId);
        return;
      }

      // Add an optimistic manager group
      draft.push({
        id: optimisticGroupId,
        // Doesn't matter
        company_id: asCompanyId(uuidv4()),
        name: "New team",
        manager_id: managerId,
        teammate_ids: [teammateId],
        created_at: new Date().toISOString(),
        updated_at: null,
        deleted_at: null,
      });
    },
  );
}

function handleSetTeammateManagerResultAction(
  queryArgs: AllTeammateGroupsArgs,
  optimisticGroupId: GroupId,
  affectedGroups: readonly Group[],
) {
  return groupApi.util.updateQueryData(
    "allTeammateGroups",
    queryArgs,
    (draft) => {
      // Remove the optimistic group if it was created
      const optimisticIndex = draft.findIndex(
        (g) => g.id === optimisticGroupId,
      );
      if (optimisticIndex !== -1) {
        draft.splice(optimisticIndex, 1);
      }

      // Upsert any of the affected groups returned by the server. A cached
      // entry keeps its own members: the response lists active members only,
      // while an `includeDeactivated` entry holds more, and this mutation
      // moves nobody but `teammateId` — whom the optimistic update already
      // placed correctly in every entry.
      for (const group of affectedGroups) {
        const index = draft.findIndex((g) => g.id === group.id);
        const cached = index !== -1 ? draft[index] : undefined;
        if (cached) {
          draft[index] = { ...group, teammate_ids: [...cached.teammate_ids] };
        } else {
          draft.push(group);
        }
      }
    },
  );
}

export const {
  useAllTeammateGroupsQuery,
  useCreateTeammateGroupMutation,
  useUpdateTeammateGroupMutation,
  useDeleteTeammateGroupMutation,
  useSetTeammateManagerMutation,
} = groupApi;
